import { useEffect } from 'react';

/**
 * POSManifestSwapper — when a cashier visits /cashier or /cashier/pos,
 * we swap the web-app manifest from the generic "Smarto" one to a
 * POS-only manifest whose `start_url` points to /cashier (the PIN
 * keypad). On iOS Safari this matters because once the user does
 * "Share → Add to Home Screen" the icon will launch straight into the
 * PIN page in standalone (no browser chrome), not the main marketing
 * site that lives at "/".
 *
 * Apple-specific tags (`apple-mobile-web-app-title`, status bar style)
 * are also retitled so the iPad home-screen icon reads "POS".
 */
export default function POSManifestSwapper() {
  useEffect(() => {
    const head = document.head;

    const previous = {
      manifest: null,
      title: null,
      bar: null,
      themeColor: null,
      pageTitle: document.title,
    };

    const swap = (selector, newAttrs, create) => {
      let el = head.querySelector(selector);
      const prev = el ? Object.fromEntries(el.getAttributeNames().map(n => [n, el.getAttribute(n)])) : null;
      if (!el && create) {
        el = create();
        head.appendChild(el);
      }
      if (el) {
        Object.entries(newAttrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
      return { el, prev };
    };

    // 1) <link rel="manifest" href="/manifest-pos.json">
    previous.manifest = swap(
      'link[rel="manifest"]',
      { rel: 'manifest', href: '/manifest-pos.json' },
      () => document.createElement('link'),
    );

    // 2) <meta name="apple-mobile-web-app-title" content="POS">
    previous.title = swap(
      'meta[name="apple-mobile-web-app-title"]',
      { name: 'apple-mobile-web-app-title', content: 'POS' },
      () => document.createElement('meta'),
    );

    // 3) status-bar — black-translucent gives the cashier a full-bleed
    //    look so the orange POS header sits right under the iPad status bar.
    previous.bar = swap(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
      { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
      () => document.createElement('meta'),
    );

    // 4) theme-color: match POS orange so the iOS status-bar background tints right.
    previous.themeColor = swap(
      'meta[name="theme-color"]',
      { name: 'theme-color', content: '#f97316' },
      () => document.createElement('meta'),
    );

    document.title = 'Smarto POS';

    return () => {
      // Restore the generic manifest + titles when the cashier leaves
      // these routes (or unmounts) so the rest of the ERP keeps its
      // own home-screen identity.
      const restore = ({ el, prev }) => {
        if (!el || !prev) return;
        // Reset attributes to the previously captured snapshot.
        el.getAttributeNames().forEach(n => {
          if (!(n in prev)) el.removeAttribute(n);
        });
        Object.entries(prev).forEach(([k, v]) => el.setAttribute(k, v));
      };
      restore(previous.manifest);
      restore(previous.title);
      restore(previous.bar);
      restore(previous.themeColor);
      document.title = previous.pageTitle;
    };
  }, []);

  return null;
}
